// src/Dashboard/pages/user/Inscription/components/DocumentsDrawer.jsx

import React, { useEffect, useState } from "react";
import {
  FaFileMedical,
  FaFileSignature,
  FaFilePdf,
  FaSave,
  FaTrashAlt,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

import DrawerShell from "./DrawerShell";
import {
  deleteDossierFile,
  updateDossierInscription,
  uploadCertificatMedical,
  uploadFormulaireLicenceSigne,
  uploadLicenceDefinitive,
} from "../../../../../services/dossierServices";

import {
  DOSSIER_DOCUMENTS_CONFIG,
  getLicenceInsuranceUrl,
  getLicenceSignedUrl,
  openInNewTab,
  toInputDate,
} from "../utils/inscriptionUtils";

function UploadBlock({
  title,
  subtitle,
  icon,
  currentUrl,
  mode,
  setMode,
  file,
  setFile,
  url,
  setUrl,
  children,
  onSave,
  onDelete,
  saving,
}) {
  return (
    <section className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-[#013E8E] dark:bg-slate-900 dark:text-indigo-300">
            {icon}
          </span>

          <div>
            <h3 className="font-bold text-slate-900 dark:text-white">
              {title}
            </h3>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {subtitle ||
                (currentUrl ? "Document déjà fourni." : "Aucun document fourni.")}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {currentUrl && (
            <button
              type="button"
              onClick={() => openInNewTab(currentUrl)}
              className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Ouvrir
            </button>
          )}

          {currentUrl && (
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-rose-700 disabled:opacity-60"
            >
              <FaTrashAlt />
              Supprimer
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div className="flex flex-wrap gap-4 text-sm text-slate-700 dark:text-slate-200">
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "upload"}
              onChange={() => setMode("upload")}
            />
            Téléverser un fichier
          </label>

          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "link"}
              onChange={() => setMode("link")}
            />
            Coller un lien
          </label>
        </div>

        {mode === "upload" ? (
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
        ) : (
          <input
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://drive.google.com/..."
            className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
        )}

        {children}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onSave}
            disabled={saving || (mode === "upload" && !file)}
            className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function DocumentsDrawer({
  isOpen,
  onClose,
  adherent,
  dossier,
  refreshDossier,
  documentsConfig = DOSSIER_DOCUMENTS_CONFIG,
}) {
  const docConfig = {
    ...DOSSIER_DOCUMENTS_CONFIG,
    ...documentsConfig,
  };

  const [saving, setSaving] = useState(false);

  const [modeCM, setModeCM] = useState("upload");
  const [fileCM, setFileCM] = useState(null);
  const [urlCM, setUrlCM] = useState("");
  const [dateCM, setDateCM] = useState("");

  const [modeSigned, setModeSigned] = useState("upload");
  const [fileSigned, setFileSigned] = useState(null);
  const [urlSigned, setUrlSigned] = useState("");

  const [modeLicence, setModeLicence] = useState("upload");
  const [fileLicence, setFileLicence] = useState(null);
  const [urlLicence, setUrlLicence] = useState("");

  const currentCM = dossier?.cm_url || "";
  const currentSigned = getLicenceSignedUrl(dossier);
  const currentLicence = getLicenceInsuranceUrl(dossier);

  useEffect(() => {
    if (!isOpen) return;

    setModeCM(
      currentCM?.startsWith?.("/uploads")
        ? "upload"
        : currentCM
        ? "link"
        : "upload"
    );
    setFileCM(null);
    setUrlCM(currentCM || "");
    setDateCM(toInputDate(dossier?.date_cm));

    setModeSigned(
      currentSigned?.startsWith?.("/uploads")
        ? "upload"
        : currentSigned
        ? "link"
        : "upload"
    );
    setFileSigned(null);
    setUrlSigned(currentSigned || "");

    setModeLicence(
      currentLicence?.startsWith?.("/uploads")
        ? "upload"
        : currentLicence
        ? "link"
        : "upload"
    );
    setFileLicence(null);
    setUrlLicence(currentLicence || "");
  }, [currentCM, currentLicence, currentSigned, dossier?.date_cm, isOpen]);

  const ensureDossier = () => {
    if (!dossier?.id) {
      toast.error("Aucun dossier d’inscription disponible pour cet adhérent.");
      return false;
    }

    return true;
  };

  const saveCM = async () => {
    if (!ensureDossier()) return;

    if (modeCM === "link" && urlCM && !/^https?:\/\//i.test(urlCM)) {
      toast.error("Lien du document médical invalide.");
      return;
    }

    try {
      setSaving(true);

      if (modeCM === "upload") {
        await uploadCertificatMedical(
          dossier.id,
          fileCM,
          dateCM ? { date_cm: dateCM } : undefined
        );
      } else {
        await updateDossierInscription(dossier.id, {
          cm_url: urlCM || null,
          date_cm: dateCM || null,
        });
      }

      await refreshDossier?.();
      toast.success("Document médical enregistré.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement du document médical.");
    } finally {
      setSaving(false);
    }
  };

  const saveCMDate = async () => {
    if (!ensureDossier()) return;

    if (!dateCM) {
      toast.warn("Indiquez la date du document médical.");
      return;
    }

    try {
      setSaving(true);
      await updateDossierInscription(dossier.id, { date_cm: dateCM });
      await refreshDossier?.();
      toast.success("Date du document médical enregistrée.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement de la date.");
    } finally {
      setSaving(false);
    }
  };

  const saveSigned = async () => {
    if (!ensureDossier()) return;

    if (modeSigned === "link" && urlSigned && !/^https?:\/\//i.test(urlSigned)) {
      toast.error("Lien du formulaire signé invalide.");
      return;
    }

    try {
      setSaving(true);

      if (modeSigned === "upload") {
        await uploadFormulaireLicenceSigne(dossier.id, fileSigned);
      } else {
        await updateDossierInscription(dossier.id, {
          formulaire_licence_signe: urlSigned || null,
          formulaire_licence_signee: urlSigned || null,
        });
      }

      await refreshDossier?.();
      toast.success("Formulaire signé enregistré.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement du formulaire signé.");
    } finally {
      setSaving(false);
    }
  };

  const saveLicence = async () => {
    if (!ensureDossier()) return;

    if (
      modeLicence === "link" &&
      urlLicence &&
      !/^https?:\/\//i.test(urlLicence)
    ) {
      toast.error("Lien licence / assurance invalide.");
      return;
    }

    try {
      setSaving(true);

      if (modeLicence === "upload") {
        await uploadLicenceDefinitive(dossier.id, fileLicence);
      } else {
        await updateDossierInscription(dossier.id, {
          licence_url: urlLicence || null,
          licence_assurance_url: urlLicence || null,
          licence_assurance: urlLicence || null,
        });
      }

      await refreshDossier?.();
      toast.success("Licence / assurance enregistrée.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement de la licence.");
    } finally {
      setSaving(false);
    }
  };

  const removeField = async (field, label) => {
    if (!ensureDossier()) return;

    const ok = window.confirm(`Supprimer « ${label} » ?`);
    if (!ok) return;

    try {
      setSaving(true);
      await deleteDossierFile(dossier.id, field);
      await refreshDossier?.();
      toast.success(`${label} supprimé.`);
    } catch (error) {
      console.error(error);
      toast.error(`Impossible de supprimer « ${label} ».`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell
      isOpen={isOpen}
      onClose={onClose}
      title="Documents d’inscription"
      subtitle={
        adherent
          ? `${adherent.prenom} ${adherent.nom}`
          : "Téléversement des documents"
      }
      icon={<FaUpload />}
      width="max-w-4xl"
    >
      {!dossier?.id ? (
        <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          Aucun dossier n’existe encore pour cet adhérent. Les documents ne
          peuvent pas être téléversés tant que le dossier n’est pas créé.
        </div>
      ) : (
        <div className="space-y-5">
          <UploadBlock
            title={
              docConfig.medicalRequired
                ? "Certificat / Attestation médicale"
                : "Certificat / Attestation médicale — optionnel"
            }
            subtitle={
              currentCM
                ? "Document déjà fourni."
                : docConfig.medicalRequired
                ? "Aucun document fourni."
                : "Document non obligatoire. Vous pouvez le déposer si nécessaire."
            }
            icon={<FaFileMedical />}
            currentUrl={currentCM}
            mode={modeCM}
            setMode={setModeCM}
            file={fileCM}
            setFile={setFileCM}
            url={urlCM}
            setUrl={setUrlCM}
            onSave={saveCM}
            onDelete={() => removeField("cm_url", "Document médical")}
            saving={saving}
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-800 dark:text-slate-100">
                  Date figurant sur le document
                </label>

                <input
                  type="date"
                  value={dateCM}
                  onChange={(event) => setDateCM(event.target.value)}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />

                {!docConfig.medicalDateRequired && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    La date est optionnelle pour ce club.
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={saveCMDate}
                disabled={saving || !dateCM}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60"
              >
                <FaSave />
                Date seule
              </button>
            </div>
          </UploadBlock>

          {docConfig.showLicenceSigned && (
            <UploadBlock
              title="Formulaire de licence signé"
              icon={<FaFileSignature />}
              currentUrl={currentSigned}
              mode={modeSigned}
              setMode={setModeSigned}
              file={fileSigned}
              setFile={setFileSigned}
              url={urlSigned}
              setUrl={setUrlSigned}
              onSave={saveSigned}
              onDelete={() =>
                removeField("formulaire_licence_signe", "Formulaire signé")
              }
              saving={saving}
            />
          )}

          {docConfig.showLicenceAssurance && (
            <UploadBlock
              title="Licence / Assurance"
              icon={<FaFilePdf />}
              currentUrl={currentLicence}
              mode={modeLicence}
              setMode={setModeLicence}
              file={fileLicence}
              setFile={setFileLicence}
              url={urlLicence}
              setUrl={setUrlLicence}
              onSave={saveLicence}
              onDelete={() => removeField("licence_url", "Licence / Assurance")}
              saving={saving}
            />
          )}
        </div>
      )}
    </DrawerShell>
  );
}